import request from '../../../core/wrappers/axios-wrapper';
import { S3QueryUrl } from '../../../types/constants/apiUrls/AIPrompt/S3Query';
import { RequestType } from '../../../types/enums/request-enums';

export interface S3FolderItem {
    name: string;
    key: string;
    type: string;
}

export interface S3FileItem {
    name: string;
    key: string;
    type: string;
    size: number;
    lastModified?: string;
    url?: string | null;
    /** S3 key of the thumbnail file (igloo_thumbs/... or siteye_thumbs/...). Use with GetS3ImageBase64(thumbnailKey) for preview. */
    thumbnailKey?: string | null;
}

export interface S3ListResponse {
    prefix: string;
    folders: S3FolderItem[];
    files: S3FileItem[];
    isTruncated: boolean;
    nextContinuationToken?: string | null;
}

export interface GetS3ListParams {
    prefix?: string;
    continuationToken?: string;
}

function mapS3List(raw: Record<string, unknown>): S3ListResponse {
    const foldersRaw = (raw.folders ?? raw.Folders ?? []) as Record<string, unknown>[];
    const filesRaw = (raw.files ?? raw.Files ?? []) as Record<string, unknown>[];
    return {
        prefix: (raw.prefix ?? raw.Prefix ?? '') as string,
        folders: foldersRaw.map((f) => ({
            name: (f.name ?? f.Name ?? '') as string,
            key: (f.key ?? f.Key ?? '') as string,
            type: (f.type ?? f.Type ?? 'folder') as string,
        })),
        files: filesRaw.map((f) => ({
            name: (f.name ?? f.Name ?? '') as string,
            key: (f.key ?? f.Key ?? '') as string,
            type: (f.type ?? f.Type ?? 'file') as string,
            size: Number(f.size ?? f.Size ?? 0),
            lastModified: (f.lastModified ?? f.LastModified) as string | undefined,
            url: (f.url ?? f.Url) as string | null | undefined,
            thumbnailKey: (f.thumbnailKey ?? f.ThumbnailKey) as string | null | undefined,
        })),
        isTruncated: Boolean(raw.isTruncated ?? raw.IsTruncated),
        nextContinuationToken: (raw.nextContinuationToken ?? raw.NextContinuationToken) as string | null | undefined,
    };
}

export const GetS3List = async (params?: GetS3ListParams): Promise<S3ListResponse> => {
    const raw = (await request({
        url: S3QueryUrl.List,
        method: RequestType.GET,
        params: {
            prefix: params?.prefix ?? '',
            continuationToken: params?.continuationToken,
        },
    })) as Record<string, unknown>;
    return mapS3List(raw);
};

export interface S3PresignedDownloadItem {
    key: string;
    fileName: string;
    url: string;
    expiresAtUtc?: string;
}

export interface S3PresignedDownloadResponse {
    items: S3PresignedDownloadItem[];
}

/** http(s)/gs:// vb. — gelen link doğrudan kullanılır, S3 key'e çevirilmez / yeniden imzalanmaz. */
const isDirectUrl = (value: string) => /^[a-z][a-z0-9+.-]*:\/\//i.test(value);

const fileNameFromUrlOrKey = (value: string) => {
    try {
        if (/^https?:\/\//i.test(value)) {
            const path = decodeURIComponent(new URL(value).pathname);
            const name = path.split('/').filter(Boolean).pop();
            if (name) return name;
        }
    } catch {
        // fall through
    }
    const parts = value.split('/').filter(Boolean);
    return parts[parts.length - 1] || value;
};

export const GetS3PresignedDownloadUrls = async (keys: string[]): Promise<S3PresignedDownloadResponse> => {
    const uniqueKeys = Array.from(new Set((keys ?? []).map((k) => (k ?? '').trim()).filter(Boolean)));
    const directKeys = uniqueKeys.filter(isDirectUrl);
    const relativeKeys = uniqueKeys.filter((k) => !isDirectUrl(k));

    const absoluteItems: S3PresignedDownloadItem[] = directKeys.map((key) => ({
        key,
        fileName: fileNameFromUrlOrKey(key),
        url: key,
    }));

    if (relativeKeys.length === 0) {
        return { items: absoluteItems };
    }

    const raw = (await request({
        url: S3QueryUrl.PresignedDownload,
        method: RequestType.POST,
        data: { keys: relativeKeys },
        timeout: 2 * 60 * 1000,
        skipErrorModal: true,
    })) as Record<string, unknown>;

    const itemsRaw = (raw.items ?? raw.Items ?? []) as Record<string, unknown>[];
    const relativeItems = itemsRaw.map((item) => ({
        key: String(item.key ?? item.Key ?? ''),
        fileName: String(item.fileName ?? item.FileName ?? ''),
        url: String(item.url ?? item.Url ?? ''),
        expiresAtUtc: (item.expiresAtUtc ?? item.ExpiresAtUtc) as string | undefined,
    }));

    return {
        items: [...absoluteItems, ...relativeItems],
    };
};

/** Direct S3/MinIO download via presigned URL (requires bucket CORS). */
export async function fetchBlobFromPresignedUrl(
    url: string,
    onProgress?: (loaded: number, total: number | null) => void,
    signal?: AbortSignal
): Promise<Blob> {
    const response = await fetch(url, { method: 'GET', signal, mode: 'cors' });
    if (!response.ok) {
        throw new Error(`Presigned download failed: HTTP ${response.status}`);
    }

    const totalHeader = response.headers.get('content-length');
    const total = totalHeader ? Number(totalHeader) : null;
    if (!response.body || typeof response.body.getReader !== 'function') {
        const blob = await response.blob();
        onProgress?.(blob.size, total ?? blob.size);
        return blob;
    }

    const reader = response.body.getReader();
    const chunks: Uint8Array[] = [];
    let loaded = 0;
    for (let result = await reader.read(); !result.done; result = await reader.read()) {
        if (result.value) {
            chunks.push(result.value);
            loaded += result.value.length;
            onProgress?.(loaded, total);
        }
    }

    return new Blob(chunks as BlobPart[], { type: 'application/octet-stream' });
}

export type FetchS3FileBlobOptions = {
    skipErrorModal?: boolean;
};

export const FetchS3FileBlob = (
    key: string,
    inline?: boolean,
    onDownloadProgress?: (progressEvent: any) => void,
    range?: string,
    options?: FetchS3FileBlobOptions
) =>
    request({
        url: S3QueryUrl.File,
        method: RequestType.GET,
        params: { key, ...(inline ? { inline: true } : {}), ...(range ? { range } : {}) },
        responseType: 'blob',
        skipBlobAutoOpen: true,
        timeout: 8 * 60 * 1000,
        onDownloadProgress,
        skipErrorModal: options?.skipErrorModal,
    }) as Promise<Blob>;

/** Max files selectable for parallel download in one batch. */
export const MAX_S3_SELECTION_KEYS = 1000;

export interface S3ImageBase64Response {
    base64: string;
    contentType: string;
    fileName: string;
}

export type GetS3ImageBase64Options = {
    skipErrorModal?: boolean;
};

const MAX_CONCURRENT_S3_IMAGE_LOADS = 3;
let activeS3ImageLoads = 0;
const s3ImageLoadQueue: Array<() => void> = [];

function withS3ImageLoadConcurrency<T>(fn: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const execute = () => {
            activeS3ImageLoads += 1;
            fn()
                .then(resolve, reject)
                .finally(() => {
                    activeS3ImageLoads -= 1;
                    const next = s3ImageLoadQueue.shift();
                    if (next) next();
                });
        };

        if (activeS3ImageLoads < MAX_CONCURRENT_S3_IMAGE_LOADS) {
            execute();
        } else {
            s3ImageLoadQueue.push(execute);
        }
    });
}

const failedThumbnailKeys = new Set<string>();

export const GetS3ImageBase64 = async (
    key: string,
    options?: GetS3ImageBase64Options
): Promise<S3ImageBase64Response> => {
    return withS3ImageLoadConcurrency(async () => {
        const raw = (await request({
            url: S3QueryUrl.ImageBase64,
            method: RequestType.GET,
            params: { key },
            timeout: 8 * 60 * 1000,
            skipErrorModal: options?.skipErrorModal,
        })) as Record<string, unknown>;
        return {
            base64: (raw.base64 ?? raw.Base64 ?? '') as string,
            contentType: (raw.contentType ?? raw.ContentType ?? 'application/octet-stream') as string,
            fileName: (raw.fileName ?? raw.FileName ?? '') as string,
        };
    });
};

/** Thumbnail grid için: yalnızca küçük thumb key, başarısız key'leri tekrar denemez. */
export const GetS3ThumbnailBase64 = async (thumbnailKey: string): Promise<S3ImageBase64Response> => {
    if (failedThumbnailKeys.has(thumbnailKey)) {
        throw new Error('thumbnail_unavailable');
    }

    try {
        const result = await GetS3ImageBase64(thumbnailKey, { skipErrorModal: true });
        if (!result.base64) {
            failedThumbnailKeys.add(thumbnailKey);
            throw new Error('thumbnail_empty');
        }
        return result;
    } catch (error) {
        failedThumbnailKeys.add(thumbnailKey);
        throw error;
    }
};

export function saveBlobAsFile(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

/** Sanitize a name for use as a local filesystem folder/file segment. */
export function sanitizeFsName(name: string): string {
    const cleaned = String(name || '')
        // eslint-disable-next-line
        .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '_')
        // sonar-disable-next-line S5852
        .replace(/\.+$/g, '')
        .trim();
    return cleaned || 'download';
}

export function getFolderNameFromPrefix(prefix: string): string {
    const clean = String(prefix || '')
        .replace(/\\/g, '/')
        // sonar-disable-next-line S5852
        .replace(/\/+$/, '');
    if (!clean) return 'download';
    const i = clean.lastIndexOf('/');
    return sanitizeFsName(i < 0 ? clean : clean.slice(i + 1));
}

export function canUseDirectoryPicker(): boolean {
    return (
        typeof globalThis !== 'undefined' &&
        typeof (globalThis as typeof globalThis & { showDirectoryPicker?: unknown }).showDirectoryPicker === 'function'
    );
}

/**
 * Ask the user to pick a parent folder (e.g. Downloads), then create/open `subFolderName` inside it.
 * Chromium File System Access API only — returns null if unsupported.
 */
export async function pickOrCreateDownloadSubfolder(subFolderName: string): Promise<FileSystemDirectoryHandle | null> {
    const picker = (
        globalThis as typeof globalThis & {
            showDirectoryPicker?: (options?: {
                mode?: 'read' | 'readwrite';
                id?: string;
                startIn?: 'desktop' | 'documents' | 'downloads' | 'music' | 'pictures' | 'videos';
            }) => Promise<FileSystemDirectoryHandle>;
        }
    ).showDirectoryPicker;

    if (!picker) return null;

    const parent = await picker({
        mode: 'readwrite',
        id: 's3-browser-download',
        startIn: 'downloads',
    });

    return parent.getDirectoryHandle(sanitizeFsName(subFolderName), { create: true });
}

export async function saveBlobToDirectory(
    directory: FileSystemDirectoryHandle,
    fileName: string,
    blob: Blob
): Promise<void> {
    const safeName = sanitizeFsName(fileName);
    const fileHandle = await directory.getFileHandle(safeName, { create: true });
    const writable = await fileHandle.createWritable();
    try {
        await writable.write(blob);
    } finally {
        await writable.close();
    }
}

export function getFileNameFromKey(key: string): string {
    const trimmed = key.replace(/\/$/, '');
    const i = trimmed.lastIndexOf('/');
    return i < 0 ? trimmed : trimmed.slice(i + 1);
}

/**
 * Opens an image in a new tab like AI/Mannequin (`fetchImageAsBase64` → BackgroundLabel get-image-base64): BFF base64 first, blob stream as fallback.
 */
export async function openS3ImageInNewTab(key: string) {
    try {
        const { base64, contentType } = await GetS3ImageBase64(key);
        if (base64) {
            const dataUrl = `data:${contentType || 'image/jpeg'};base64,${base64}`;
            window.open(dataUrl, '_blank', 'noopener,noreferrer');
            return;
        }
    } catch {
        // fall through to blob (e.g. timeout on very large object)
    }

    const blob = await FetchS3FileBlob(key, true);
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    globalThis.setTimeout(() => URL.revokeObjectURL(url), 120_000);
}
